import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query"
import EModuleIds, { ESubModuleIds } from "enums/ModuleEnum"
import { enqueueSnackbar } from "notistack"
import { api } from "../../api"
import { setAlertFunc } from "../../components/AlertToaster/AlertSlice"
import { FileObj } from "../../components/MultiFileModal"
import { TStore } from "../../store/Store"
import { getTagType } from "../../utils/utils"
import { getTicketDetails as getDemolitionTicketDetails } from "../Operations/features/Demolition/DemolitionSlice"
import { getDemolition, getDemolitionTabs, resetBulkMappingStates } from "../Operations/OperationSlice"
import {
    AccountBySiteData,
    AccountRoleObj,
    BlockedReasons,
    BOQItem,
    BOQItemsPayload,
    BulkLabelPayload,
    BulkTaskStatusResponse,
    CategoryObj,
    ChangeOrderObj,
    ChargerDetailsData,
    ChargerTypeObj,
    CPOResponseType,
    DfcApprovalResponse,
    DRBoqItem,
    GetTagRequest,
    HistoryData,
    HostingAddress,
    ICinListResponse,
    ICobrandedItem,
    ICountry,
    IDocHubDocument,
    IDocType,
    IFile,
    IResponse,
    LabelResponse,
    Product,
    ProjectCategoryOption,
    SlackChannel,
    SlackUser,
    StandardItems,
    TagTypes,
    TPurchaseOrder,
    UserInfo,
    VendorBill,
    VendorBillsResponse,
    VendorPayment,
    VendorPaymentsResponse,
    ReasonsObj
} from "./commonTypes"

const commonService = api.injectEndpoints({
    endpoints: build => ({
        getStates: build.query<{ id: number; name: string; region: string }[], void>({
            query: () => ({
                url: `states`,
            }),
            transformResponse: (response: IResponse<any[]>) => response.data,
        }),
        getCities: build.query<{ id: number; name: string; state_id: number }[], { state_id: number[] }>({
            query: ({ state_id }) => ({
                url: `cities`,
                params: {
                    state_id: state_id.join(","),
                },
            }),
        }),
        getCountries: build.query<{ count: number; status: string; data: ICountry[] }, void>({
            query: () => ({
                url: `countries`,
            }),
        }),
        getCountryStates: build.query<
            { id: number; name: string; region: string }[],
            { country_id?: number; zone?: string[] }
        >({
            query: params => ({
                url: `states`,
                params:
                    params?.zone && params?.zone?.length > 0
                        ? {
                              country_id: params?.country_id ?? 101,
                              zone: JSON.stringify(params?.zone ?? []),
                          }
                        : {
                              country_id: params?.country_id ?? 101,
                          },
            }),
            transformResponse: (response: IResponse<any[]>) => response.data,
        }),
        // Query to get charger details associated with a ticket
        getChargerDetails: build.query<ChargerDetailsData[], { ticket_id: string; ticket_type: string }>({
            query: params => ({
                url: `charger_details`,
                params: {
                    ticket_id: params.ticket_id,
                    ticket_type: params.ticket_type,
                },
            }),
            transformResponse: (response: IResponse<ChargerDetailsData[]>) => response.data,
        }),
        getChargerDetailsV3: build.query<ChargerDetailsData[], { ticket_id: string; ticket_type: string }>({
            query: params => ({
                url: `charger_details`,
                params: {
                    ticket_id: params.ticket_id,
                    ticket_type: params.ticket_type,
                },
                useV3: true,
            }),
            transformResponse: (response: IResponse<ChargerDetailsData[]>) => response.data,
        }),
        // Query to get all charger types available
        getChargersType: build.query<ChargerTypeObj[], void>({
            query: () => ({
                url: `chargers`,
            }),
        }),
        // Query to get all statuses of a particular module
        getAllStatus: build.query<{ id: number; name: string }[], { mod_id: number; sub_module: string }>({
            query: params => ({
                url: `childstatus`,
                params: params,
            }),
        }),
        // Query to fetch OEM filter options
        getOEM: build.query<IResponse<{ id: number; name: string }[]>, void>({
            query: () => ({
                url: `oem`,
            }),
        }),
        // Query to fetch CPO filter options -- TO BE DEPRECIATED
        getCPO: build.query<CPOResponseType, { search_key?: string; category_id?: number | "" } | void>({
            query: () => ({
                url: `onboarding/host/listcpo`,
            }),
        }),

        // New API
        getCpoList: build.query<
            { account_id: number; account_name: string }[],
            { search_key?: string; category_id?: number | "" } | void
        >({
            query: () => ({
                url: `projects/cpos`,
                useV3: true,
            }),
            transformResponse: (data: { data: { account_id: number; account_name: string }[] }) => data.data,
        }),
        getHostNames: build.query<any, void>({
            query: () => ({
                url: `mapped_hosts`,
                params: {
                    names: "",
                },
            }),
        }),
        getCPOAccountNames: build.query<any, void>({
            query: () => ({
                url: `mapped_cpos`,
                params: {
                    names: "",
                },
            }),
        }),
        getReasons: build.query<
            ReasonsObj[],
            { module_id: number; submodule_id?: number; reason_type?: string; only_active?: boolean }
        >({
            query: ({ only_active, ...params }) => ({
                url: `module_reasons`,
                useV3: true,
                params: {
                    ...params,
                    submodule_id: params?.submodule_id || "",
                },
            }),
            providesTags: [{ type: "Settings", id: "Reasons" }],
            transformResponse: (res: { data: ReasonsObj[] }, _meta, arg) =>
                arg?.only_active === false ? res.data : res.data?.filter(reason => reason.status === true),
        }),
        getChargerMakeDeatils: build.query<any, { ticket_id: number; ticket_type?: string }>({
            query: params => ({
                url: `make`,
                params: {
                    ...params,
                },
            }),
        }),
        getStandardItems: build.query<StandardItems[], void>({
            query: () => ({
                url: `standard_items`,
            }),
        }),
        getAmenities: build.query<{ amenity_id: number; amenity_name: string; amenity_icon: string }[], void>({
            query: () => ({
                url: `amenities`,
            }),
        }),
        getDiscoms: build.query<{ id: number; name: string }[], void>({
            query: () => ({
                url: `discoms`,
            }),
        }),
        getBrands: build.query<{ id: number; name: string }[], void>({
            query: () => ({
                url: `branding`,
            }),
            transformResponse: (response: IResponse<{ id: number; name: string }[]>) => response.data,
        }),
        getUOM: build.query<{ id: number; name: string }[], void>({
            query: () => ({
                url: `uom`,
            }),
            transformResponse: (response: IResponse<{ id: number; name: string }[]>) => response.data,
        }),
        getChargerMake: build.query<{ id: number; name: string }[], void>({
            query: () => ({
                url: `make`,
            }),
            transformResponse: (response: IResponse<{ id: number; name: string }[]>) => response.data,
        }),
        getConnectors: build.query<{ id: number; name: string }[], void>({
            query: () => ({
                url: `connectors`,
            }),
            transformResponse: (response: IResponse<{ id: number; name: string }[]>) => response.data,
        }),
        getAccountRoles: build.query<AccountRoleObj[], void>({
            query: () => ({
                url: `get_account_types`,
                useV3: true,
            }),
        }),
        // Query to fetch assigned-to filter options
        getAssignedToFilterOptions: build.query<
            Partial<UserInfo[]>,
            { search_key?: string; ticket_id?: string; module_id?: number; submodule_id?: number | "" }
        >({
            queryFn: async (params, { getState }, extraOptions, baseQuery) => {
                const state = getState() as any
                const role_id = state?.verifyLogin?.loginData?.user_data?.role_id

                const response = await baseQuery({
                    url: `get_assign_to`,
                    params: {
                        ...params,
                        role_id: role_id ?? "",
                    },
                })

                if (response.error) {
                    return { error: response.error as FetchBaseQueryError }
                }

                return { data: response.data as Partial<UserInfo[]> }
            },
        }),

        //Query to fetch cins with or without sku_code and product_id
        getCinsBySkuAndProduct: build.query<
            ICinListResponse,
            { sku_code?: string; product_id?: number | number[]; page?: number; page_size?: number; search?: string }
        >({
            query: ({ product_id, ...rest }) => {
                const searchParams = new URLSearchParams()
                Object.entries(rest).forEach(([k, v]) => {
                    if (v != null) searchParams.append(k, String(v))
                })
                if (Array.isArray(product_id)) {
                    product_id.forEach(id => searchParams.append("product_id", String(id)))
                } else if (product_id != null) {
                    searchParams.append("product_id", String(product_id))
                }
                const qs = searchParams.toString()
                return { url: qs ? `get_cins?${qs}` : `get_cins`, useV3: true }
            },
        }),
        // Query to fetch assigned-to list for progress component
        getAssignedToListOptions: build.query<
            Partial<UserInfo[]>,
            { search_key: string; ticket_id: string; module_id: number; submodule_id?: number }
        >({
            queryFn: async (params, { getState }, extraOptions, baseQuery) => {
                const state = getState() as TStore
                const role_id = state?.verifyLogin?.loginData?.user_data?.role_id

                const response = await baseQuery({
                    url: `comments/assign_to`,
                    params: {
                        ...params,
                        submodule_id: params.submodule_id ?? "",
                        role_id: role_id ?? "",
                        useV3: true,
                    },
                })

                if (response.error) {
                    return { error: response.error as FetchBaseQueryError }
                }

                return { data: response.data as Partial<UserInfo[]> }
            },
        }),
        // Query to fetch all active users who have any access to a given module (RBAC-scoped)
        getModuleUsers: build.query<
            Omit<UserInfo, "name">[],
            { module_id: number; submodule_id?: number; search_key?: string }
        >({
            query: ({ module_id, submodule_id, search_key }) => ({
                url: `module_users`,
                useV3: true,
                params: {
                    module_id,
                    ...(submodule_id ? { submodule_id } : {}),
                    ...(search_key ? { search_key } : {}),
                },
            }),
        }),
        getVendorList: build.query<
            { id: number; name: string }[],
            { search_key?: string; category_id?: number | ""; team_id?: number }
        >({
            query: ({ search_key = "", category_id = "", team_id = "" }) => ({
                url: `third_party/vendor_list`,
                params: {
                    search_key,
                    category_id,
                    team_id: team_id ?? "",
                },
            }),
        }),
        getCategories: build.query<
            CategoryObj[],
            { module_id: number; is_visible?: boolean; team_id?: number; modules_flag?: boolean }
        >({
            query: params => ({
                url: `categories`,
                params: {
                    ...params,
                    is_visible: params?.is_visible ? "True" : params.module_id === 2 ? "False" : "True",
                },
            }),
        }),

        getDocumentTypes: build.query<IDocType[], void>({
            query: () => ({
                url: `document_types`,
                useV3: true,
            }),
            transformResponse: (response: IResponse<IDocType[]>) => response.data,
        }),
        getDocumentsData: build.query<
            IDocHubDocument[],
            {
                site_id?: number
                doc_type?: string | number
                module_id?: EModuleIds
                submodule_id?: ESubModuleIds
                ticket_id?: number | string
                uploaded_by?: number | string
            }
        >({
            query: params => ({
                url: `get_documents`,
                useV3: true,
                params,
            }),
            transformResponse: (response: IResponse<IDocHubDocument[]>) => response.data,
            providesTags: ["Sites", "Survey", "Installation", "Demolition", "EvApplication"],
        }),
        // Query to fetch charger SKUs and name, id
        getChargerSku: build.query<
            { charger_type_id: number; name: string; sku: string; oem_name: string; oem_id: number }[],
            void
        >({
            query: () => ({
                url: `get_charger_skus`,
            }),
        }),
        // Query to fetch time of day
        getTimeOfDay: build.query<IResponse<{ id: number; name: string }[]>, void>({
            query: () => ({
                url: `time_of_day`,
            }),
        }),

        getDealDoneByUsers: build.query<{ id: number; name: string }[], void>({
            query: () => ({
                url: `operations/deal_done_by_users`,
            }),
        }),

        getDocumentTemplates: build.query<IResponse<IFile>, { category_id: string }>({
            query: ({ category_id }) => ({
                url: `document_templates`,
                params: {
                    category_id,
                },
            }),
        }),

        // Query to get hosting address corresponding to the city and state
        getHostingAddress: build.query<HostingAddress[], { city_id?: string; state_id: string }>({
            query: ({ city_id, state_id }) => ({
                url: `hosting_address`,
                params: {
                    city_id: city_id ?? "",
                    state_id,
                },
            }),
            transformResponse: (response: IResponse<HostingAddress[]>) => response.data,
        }),

        // For Blocked reasons list
        getReasonsForBlocking: build.query<
            BlockedReasons[],
            { module_id: string; reason_type: string; submodule_id: string | number }
        >({
            query: params => ({
                url: `settings/blocked_reasons`,
                params: params,
            }),
            providesTags: ["Settings"],
        }),

        getSurveyLinkedToProject: build.query<{ survey_id: string; survey_name: string }[], { project_id: string }>({
            query: params => ({
                url: `fetch_surveys`,
                params,
            }),
            providesTags: ["Survey", "Installation", "Projects"],
        }),

        getBOQ: build.query<any, { ticket_id: string; project_id: string }>({
            query: params => ({
                url: `cristi_boq`,
                useV3: true,
                params,
            }),
            transformResponse: (response: any) => (Object.keys(response)?.length > 0 ? response : undefined),
            providesTags: [
                { type: "Survey", id: "BOQ" },
                { type: "Installation", id: "BOQ" },
            ],
        }),
        fetchExistingBOQs: build.query<any, void>({
            query: () => ({
                url: `fetch_existing_boq`,
                useV3: true,
            }),
            providesTags: [
                { type: "Survey", id: "BOQ" },
                { type: "Installation", id: "BOQ" },
            ],
        }),
        getBoqGrandTotal: build.query<
            {
                grand_total: string
                items: BOQItem[]
                v1_fallback_total: string
                v2_total: string
            },
            { project_id: string }
        >({
            query: params => ({
                url: `boq_grand_total`,
                params,
                useV3: true,
            }),
            transformResponse: (response: any) => (Object.keys(response)?.length > 0 ? response : undefined),
            providesTags: [
                { type: "Survey", id: "BOQ" },
                { type: "Installation", id: "BOQ" },
            ],
        }),

        // For ops ticket history
        getOpsHistory: build.query<HistoryData[], { ticket_id: string; sub_module: string }>({
            query: params => ({
                url: `operations/ticket_history`,
                params,
            }),
            providesTags: (result, error, arg) => [getTagType(arg.sub_module) as TagTypes],
        }),

        // Get all labels
        getAllLabels: build.query<LabelResponse, void>({
            query: () => ({
                url: `label`,
            }),
            providesTags: ["Labels"],
        }),

        // Labels for a particular ticket
        getLabels: build.query<
            LabelResponse,
            { ticket_id?: number | ""; module_id: number; submodule_id: number; search_term?: string }
        >({
            query: params => ({
                url: `label`,
                params: {
                    ...params,
                },
            }),
            providesTags: ["Labels"],
        }),

        getGrnDetails: build.query<any, { ticket_id: string; boq_id: string; item_id: string }>({
            query: params => ({
                url: `get_grn_details`,
                params,
            }),
            providesTags: [
                { type: "Survey", id: "BOQ" },
                { type: "Installation", id: "BOQ" },
            ],
        }),

        getAllSitesList: build.query<
            {
                name: string
                site_id: string
                id: number
                is_warehouse: 0 | 1
            }[],
            void
        >({
            query: () => ({
                url: `get_sites_compacted`,
                useV3: true,
            }),
            transformResponse: (response: {
                data: { name: string; site_id: string; id: number; is_warehouse: 0 | 1 }[]
                have_more: boolean
                page: number
                page_size: number
            }) => response.data,
            providesTags: ["Sites"],
        }),

        getAllAccountsList: build.query<
            {
                account_name: string
                id: number
                crm_account_id: string
            }[],
            Record<string, any> | void
        >({
            query: params => ({
                url: `get_accounts_compacted`,
                useV3: true,
                params: params || {},
            }),
            transformResponse: (response: {
                data: { account_name: string; id: number; crm_account_id: string }[]
                have_more: boolean
                page: number
                page_size: number
            }) => response.data,
            providesTags: ["Accounts"],
        }),

        getAccountsCompacted: build.query<
            {
                data: { id: number; account_name: string; crm_account_id: string }[]
                have_more: boolean
                page: number
                page_size: number
            },
            { page: number; page_size: number; search_key?: string; account_type?: string; sortBy?: string }
        >({
            query: params => ({
                url: `get_accounts_compacted`,
                useV3: true,
                params,
            }),
        }),

        getSitesCompacted: build.query<
            {
                data: { id: number; site_id: string; name: string; is_warehouse: 0 | 1 }[]
                have_more: boolean
                page: number
                page_size: number
            },
            { page: number; page_size: number; search?: string; prioritize_warehouses?: boolean }
        >({
            query: params => ({
                url: `get_sites_compacted`,
                useV3: true,
                params,
            }),
        }),
        getAccountBySiteId: build.query<
            AccountBySiteData,
            {
                site_id?: number
            }
        >({
            query: ({ site_id }) => ({
                url: `get_accounts_by_site`,
                useV3: true,
                params: {
                    site_id,
                },
            }),
            transformResponse: (response: { data: AccountBySiteData; success: boolean }) => response.data,
            providesTags: ["Accounts"],
        }),

        getCoBrandedList: build.query<ICobrandedItem[], void>({
            query: () => ({
                url: `cobranded_companies`,
                useV3: true,
            }),
            transformResponse: (response: { data: ICobrandedItem[] }) => response.data,
            providesTags: ["Projects"],
        }),

        getAllProgramsList: build.query<any, void>({
            query: () => ({
                url: `get_programs`,
                useV3: true,
            }),
            transformResponse: (res: { programs: any[] }) => res.programs,
            providesTags: ["Programs"],
        }),

        // The project category master. Pass include_system for the filter drawers — system
        // categories are written only by automations, so they are hidden from raise-form
        // pickers but must stay filterable or that volume becomes unreportable.
        getProjectCategories: build.query<
            ProjectCategoryOption[],
            { include_inactive?: boolean; include_system?: boolean } | void
        >({
            query: params => ({
                url: `projects/categories`,
                params: {
                    ...(params?.include_inactive ? { include_inactive: true } : {}),
                    ...(params?.include_system ? { include_system: true } : {}),
                },
                useV3: true,
            }),
            transformResponse: (res: { categories: ProjectCategoryOption[] }) => res.categories ?? [],
        }),

        getProducts: build.query<
            Product[],
            void | {
                is_material?: 0 | 1
                is_charger?: 0 | 1
                user_id?: number
                operational_sku_filter?: 0 | 1
            }
        >({
            query: params => ({
                url: `get_products`,
                params: params ?? undefined,
                useV3: true,
            }),
            transformResponse: (response: { products: Product[] }) => response.products,
        }),
        getProductsCompacted: build.query<
            { products: Product[]; page: number; per_page: number; have_more: boolean },
            { page: number; per_page: number; search?: string }
        >({
            query: params => ({
                url: `get_products_compacted`,
                params,
                useV3: true,
            }),
        }),

        // Get all labels
        getAllTags: build.query<{ data: GetTagRequest[]; total_tags: number; total_pages: number }, void>({
            query: () => ({
                url: `tags/tags_operations`,
            }),
            providesTags: ["Tags", "Installation"],
        }),
        getModuleTags: build.query<
            any,
            { module_id: number; ticket_id: string; module: string; submodule_id?: number }
        >({
            query: ({ ticket_id, module_id, module, submodule_id }) => ({
                url: `get_ticket_tags`,
                useV3: true,
                params: {
                    module_id: module_id,
                    ticket_id,
                    module,
                    submodule_id: submodule_id || "",
                },
            }),
            providesTags: (result, error, arg) => [
                { type: "Tags", id: arg.ticket_id },
                getTagType(arg.module) as TagTypes,
                "Installation",
            ],
        }),
        getDrBoqMaterials: build.query<
            {
                items: DRBoqItem[]
            },
            { project_id: string; ticket_id?: string }
        >({
            query: ({ ticket_id, ...params }) => ({
                url: `dispatch_request/dr_boq`,
                params: {
                  ...params,
                  ...(ticket_id ? { dr_id: ticket_id } : {}),
                  status: "approved"
                },
                useV3: true,
            }),
        }),

        getContactTypes: build.query<{ id: number; contact_type: string; module: string }[], void>({
            query: () => ({
                url: `contact_types`,
                useV3: true,
            }),
            transformResponse: (res: { data: { id: number; contact_type: string; module: string }[] }) =>
                res.data?.filter(i => i.module === "account") || [],
        }),

        getReverseDrBoqMaterials: build.query<any[], { project_id?: string; ticket_id?: string }>({
            query: ({ ticket_id, ...params }) => ({
                url: `dispatch_request/parsed_dr`,
                useV3: true,
                params: {
                    ...params,
                    ...(ticket_id ? { dr_id: ticket_id } : {}),
                    response_type: "total",
                    status: "delivered",
                },
            }),
            providesTags: ["Installation", "Demolition", "Logistics", "Finance"],
        }),

        getDrCreationStatus: build.query<
            {
                create_new_ticket: boolean
                replace: {
                    flag: boolean
                    message: string
                    dr_id?: number
                }
            },
            { project_id: string; source_site_id: string }
        >({
            query: params => ({
                url: `dispatch_request/check_dr_creation_status`,
                useV3: true,
                params: {
                    ...params,
                    type: "normal",
                },
            }),
        }),

        getReverseDrCreationStatus: build.query<
            {
                create_new_ticket: boolean
                replace: {
                    flag: boolean
                    message: string
                    dr_id?: number
                }
            },
            { project_id: string; destination_site_id: string }
        >({
            query: params => ({
                url: `dispatch_request/check_dr_creation_status`,
                useV3: true,
                params: {
                    ...params,
                    type: "reverse",
                },
            }),
        }),

        getDemolitionDrCreationStatus: build.query<
            {
                create_new_ticket: boolean
                replace: {
                    flag: boolean
                    message: string
                    dr_id?: number
                }
            },
            { project_id: string; destination_site_id: string; ticket_id: string }
        >({
            query: params => ({
                url: `dispatch_request/check_dr_creation_status`,
                useV3: true,
                params: {
                    ...params,
                    type: "demolition",
                },
            }),
        }),

        getParsedDr: build.query<
            any,
            {
                project_id?: string
                ticket_id?: string
                response_type?: "ticket_wise" | "total"
                for_demolition?: boolean
                is_reverse?: 0 | 1
            }
        >({
            query: ({ ticket_id, ...params }) => ({
                url: `dispatch_request/parsed_dr`,
                useV3: true,
                params: {
                    ...params,
                    ...(ticket_id ? { dr_id: ticket_id } : {}),
                },
            }),
            providesTags: ["Installation", "Demolition", "Logistics", "Finance"],
        }),

        getPurchaseOrders: build.query<
            TPurchaseOrder[],
            void | {
                po_id?: number
                po_type?: "receivable" | "payable"
                account_id?: number
                vendor_id?: number
                status?: "Open" | "Closed" | "Excess" | "Short_closed"
                project_id?: number
                sub_ticket_id?: number
                module_id?: EModuleIds
                submodule_id?: ESubModuleIds
                include_items?: 0 | 1
                show_ticket_mapping?: 0 | 1
            }
        >({
            query: params => ({
                url: `purchase_orders`,
                useV3: true,
                params: params ?? undefined,
            }),
            transformResponse: (response: { data: TPurchaseOrder[]; status?: boolean; success?: boolean }) =>
                response?.data ?? [],
            providesTags: ["Projects"],
        }),

        downloadParsedDr: build.query<
            {
                link: string
            },
            {
                project_id: string
                ticket_id?: string
                response_type?: "ticket_wise" | "total"
                is_reverse?: 0 | 1
            }
        >({
            query: ({ ticket_id, ...params }) => ({
                url: `dispatch_request/download_parsed_dr`,
                params: {
                    ...params,
                    ...(ticket_id ? { dr_id: ticket_id } : {}),
                },
                useV3: true,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
                try {
                    const { data } = await queryFulfilled
                    window.open(data.link, "_blank")
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.error?.data?.error || "Something went wrong!" }))
                }
            },
        }),

        //  map a tag
        mapTagToTicket: build.mutation<
            { msg: string; ttm_id: number },
            {
                tag_id: number
                ticket_id: number
                tag_name: string
                is_primary: number
                color_code: string
                text_color_code: string
                module: string
                user_id: number
                ticket_type?: string
                view: string
            }
        >({
            query: payload => ({
                url: `tags/tag_mapping_operations`,
                method: "POST",
                body: payload,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
                try {
                    const { data } = await queryFulfilled
                    const state: any = getState()
                    const { DemolitionFormValue, page: demoPage, selectedValue: demoSelectedValue } = state.operation
                    enqueueSnackbar(data?.msg, { variant: "success" })
                    if (arg?.ticket_type === "Demolition") {
                        if (arg?.view === "table") {
                            dispatch(
                                getDemolitionTabs({
                                    data: DemolitionFormValue,
                                    page: demoPage,
                                    selectedValue: demoSelectedValue,
                                })
                            )
                            dispatch(
                                getDemolition({
                                    data: DemolitionFormValue,
                                    page: demoPage,
                                    selectedValue: demoSelectedValue,
                                })
                            )
                        } else {
                            dispatch(getDemolitionTicketDetails(arg.ticket_id))
                        }
                    }
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.msg || "Something went wrong!", { variant: "error" })
                }
            },
            invalidatesTags: (result, error, arg) =>
                arg.module === "onb" && arg.view === "detail"
                    ? [{ type: "Tags", id: arg.ticket_id }, "Installation"]
                    : arg?.ticket_type
                      ? [
                            getTagType(arg.ticket_type) as TagTypes,
                            "Logistics",
                            "Installation",
                            "EvApplication",
                            "Survey",
                            "Projects",
                            "Onboarding",
                            "DispatchRequest"
                        ]
                      : [],
        }),
        // Create a tag
        createTagToTicket: build.mutation<
            { msg: string; ttm_id: number },
            {
                tag_id: number
                ticket_id: number
                name: string
                color_code: string
                text_color_code: string
                tag_name: string
                is_primary: number
                module: string
                user_id: number
                ticket_type?: string
                view: string
            }
        >({
            query: payload => ({
                url: `tags/tag_mapping_operations`,
                method: "POST",
                body: payload,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
                try {
                    const { data } = await queryFulfilled
                    const state: any = getState()

                    const { DemolitionFormValue, page: demoPage, selectedValue: demoSelectedValue } = state.operation
                    enqueueSnackbar(data?.msg, { variant: "success" })
                    if (arg?.ticket_type === "Demolition") {
                        if (arg?.view === "table") {
                            dispatch(
                                getDemolitionTabs({
                                    data: DemolitionFormValue,
                                    page: demoPage,
                                    selectedValue: demoSelectedValue,
                                })
                            )
                            dispatch(
                                getDemolition({
                                    data: DemolitionFormValue,
                                    page: demoPage,
                                    selectedValue: demoSelectedValue,
                                })
                            )
                        } else {
                            dispatch(getDemolitionTicketDetails(arg.ticket_id))
                        }
                    }
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.msg || "Something went wrong!", { variant: "error" })
                }
            },
            invalidatesTags: (result, error, arg) =>
                arg.module === "onb" && arg.view === "detail"
                    ? [{ type: "Tags", id: arg.ticket_id }, "Tags", "Installation"]
                    : arg?.ticket_type
                      ? [
                            "Tags",
                            getTagType(arg.ticket_type) as TagTypes,
                            "Logistics",
                            "Stations",
                            "Installation",
                            "EvApplication",
                            "Survey",
                            "Projects",
                            "Onboarding",
                            "DispatchRequest"
                        ]
                      : ["Tags"],
        }),

        // Update primary tag
        updatePrimaryTag: build.mutation<
            { msg: string },
            {
                tag_id: number
                ticket_id: number
                tag_name: string
                is_primary: boolean
                module: string
                ticket_type: string
                user_id: number
                view: "table" | "detail"
            }
        >({
            query: payload => ({
                url: `tags/tag_mapping_operations`,
                method: "PUT",
                body: payload,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
                try {
                    const { data } = await queryFulfilled
                    const state: any = getState()
                    const { DemolitionFormValue, page: demoPage, selectedValue: demoSelectedValue } = state.operation
                    enqueueSnackbar(data?.msg, { variant: "success" })
                    if (arg?.ticket_type === "Demolition") {
                        if (arg?.view === "table") {
                            dispatch(
                                getDemolitionTabs({
                                    data: DemolitionFormValue,
                                    page: demoPage,
                                    selectedValue: demoSelectedValue,
                                })
                            )
                            dispatch(
                                getDemolition({
                                    data: DemolitionFormValue,
                                    page: demoPage,
                                    selectedValue: demoSelectedValue,
                                })
                            )
                        } else {
                            dispatch(getDemolitionTicketDetails(arg.ticket_id))
                        }
                    }
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.msg || "Something went wrong!", { variant: "error" })
                }
            },
            invalidatesTags: (result, error, arg) =>
                arg.module === "onb" && arg.view === "detail"
                    ? [{ type: "Tags", id: arg.ticket_id }, "Installation"]
                    : arg?.ticket_type
                      ? [
                            getTagType(arg.ticket_type) as TagTypes,
                            "Logistics",
                            "Installation",
                            "EvApplication",
                            "Survey",
                            "Projects",
                            "Onboarding",
                            "DispatchRequest"
                        ]
                      : [],
        }),
        // Delete (soft delete) tag from ticket
        unmapTagFromTicket: build.mutation<
            { msg: string },
            {
                tag_id: number
                ticket_id: number
                tag_name: string
                status: 0
                module: string
                user_id: number
                ticket_type: string
                view: "table" | "detail"
            }
        >({
            query: payload => ({
                url: `tags/tag_mapping_operations`,
                method: "DELETE",
                body: payload,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
                try {
                    const { data } = await queryFulfilled
                    const state: any = getState()

                    const { DemolitionFormValue, page: demoPage, selectedValue: demoSelectedValue } = state.operation
                    enqueueSnackbar(data?.msg, { variant: "success" })
                    if (arg?.ticket_type === "Demolition") {
                        if (arg?.view === "table") {
                            dispatch(
                                getDemolitionTabs({
                                    data: DemolitionFormValue,
                                    page: demoPage,
                                    selectedValue: demoSelectedValue,
                                })
                            )
                            dispatch(
                                getDemolition({
                                    data: DemolitionFormValue,
                                    page: demoPage,
                                    selectedValue: demoSelectedValue,
                                })
                            )
                        } else {
                            dispatch(getDemolitionTicketDetails(arg.ticket_id))
                        }
                    }
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.msg || "Something went wrong!", { variant: "error" })
                }
            },
            invalidatesTags: (result, error, arg) =>
                arg.module === "onb" && arg.view === "detail"
                    ? [{ type: "Tags", id: arg.ticket_id }, "Installation"]
                    : [
                          getTagType(arg.ticket_type) as TagTypes,
                          "Logistics",
                          "Installation",
                          "EvApplication",
                          "Survey",
                          "Projects",
                          "Onboarding",
                          "DispatchRequest"
                      ],
        }),

        // To get the file view url by sending the presigned url
        getFileViewUrl: build.mutation<{ message: IFile }, string>({
            query: presignedUrl => ({
                url: `get_presigned_view`,
                method: "POST",
                body: {
                    url: presignedUrl,
                },
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    const previewUrl = data.message.urls.view
                    window.open(previewUrl, "_blank")
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message }))
                }
            },
        }),
        getProgressHistory: build.query<any, { ticket_id: string }>({
            query: ({ ticket_id }) => ({
                url: `operations/completion_progress`,
                params: {
                    ticket_id: ticket_id,
                },
            }),
            providesTags: ["Installation"],
        }),
        updateProgressPercentage: build.mutation<IResponse<string>, FormData>({
            query: body => ({
                url: `operations/completion_progress`,
                method: "PUT",
                body: body,
            }),
            invalidatesTags: ["Installation"],
        }),
        getSlackChannels: build.query<SlackChannel[], void>({
            query: () => ({
                url: `fetch_slack_channels`,
            }),
        }),

        getSlackUsers: build.query<SlackUser[], void>({
            query: () => ({
                url: `fetch_slack_users`,
            }),
        }),

        getRoles: build.query<{ id: number; type: string }[], void>({
            query: () => ({
                url: `roles`,
            }),
            transformResponse: (res: { data: { id: number; type: string }[] }) => res.data,
        }),
        getStakeholdersRole: build.query<{ id: number; type: string }[], void>({
            query: () => ({
                url: `roles`,
                useV3: true,
            }),
            transformResponse: (res: { data: { id: number; type: string }[] }) => res.data,
        }),
        // To get the scope of work groups for infra (Project Form)
        getInfraSows: build.query<{ id: number; name: string }[], void>({
            query: () => ({
                url: `/get_scope_of_work_groups`,
                useV3: true,
            }),
            transformResponse: (res: { data: { id: number; name: string }[] }) => res.data,
        }),

        // Mutation to change the primary tag
        updatePrimaryLabel: build.mutation<
            any,
            {
                label_id: number
                is_primary: 1 | 0
                ticket_id: number
                module_id: number
                submodule_id: number
            }
        >({
            query: body => ({
                url: `label/sequence`,
                method: "PUT",
                body,
            }),
            invalidatesTags: ["Labels"],
        }),
        // Mutation to create or map labels to a particular ticket
        mapLabels: build.mutation<IResponse<string>, { data: any }>({
            query: ({ data }) => ({
                url: `label/create_or_map`,
                method: "POST",
                body: data,
            }),
            invalidatesTags: ["Labels"],
        }),
        // Mutation to clear labels
        clearLabels: build.mutation<IResponse<string>, { data: any }>({
            query: ({ data }) => ({
                url: `label/remove`,
                method: "POST",
                body: data,
            }),
            invalidatesTags: ["Labels"],
        }),

        deleteLabelMutation: build.mutation<{ message: string; status: string }, { id: number }>({
            query: ({ id }) => ({
                url: `label/${id}`,
                method: "DELETE",
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.message }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message }))
                }
            },
            invalidatesTags: ["Labels"],
        }),

        // Mutation for bulk mapping to labels to multiple ticket
        updateLabelsInBulk: build.mutation<{ message: string; status: string }, BulkLabelPayload[]>({
            query: payload => ({
                url: `label/map`,
                method: "POST",
                body: {
                    data: payload,
                },
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.message }))
                    dispatch(resetBulkMappingStates())
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message }))
                }
            },
            invalidatesTags: ["Labels"],
        }),

        // Mutation to update charger details for a particular ticket
        updateChargerDetails: build.mutation<
            IResponse<{ msg: string }>,
            {
                data: {
                    ticket_id: string
                    ticket_type: string
                    data: Partial<ChargerDetailsData[]>
                    seq_map?: Record<string, number>
                }
                removeCache?: boolean
            }
        >({
            query: ({ data, removeCache = true }) => ({
                url: `charger_details`,
                method: "PUT",
                body: data,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.data?.msg }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
            invalidatesTags: (result, error, arg) =>
                arg?.removeCache === false
                    ? []
                    : [getTagType(arg.data.ticket_type) as TagTypes, "Logistics", "Stations", "Installation"],
        }),
        updateChargerDetailsV3: build.mutation<
            IResponse<{ msg: string }>,
            {
                data: {
                    ticket_id: string
                    ticket_type: string
                    data: Partial<ChargerDetailsData[]>
                    seq_map?: Record<string, number>
                }
                removeCache?: boolean
            }
        >({
            query: ({ data, removeCache = true }) => ({
                url: `charger_details`,
                method: "PUT",
                useV3: true,
                body: data,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.data?.msg }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
            invalidatesTags: (result, error, arg) =>
                arg?.removeCache === false
                    ? []
                    : [getTagType(arg.data.ticket_type) as TagTypes, "Logistics", "Stations", "Installation"],
        }),

        // FormData -> file, category(eg: Commissioning_Report/ logistics_bulk_upload), reference_id (charger_id in case of Commissioning_Report else ticket_id)
        uploadToS3AndGetObjUrl: build.mutation<{ error: string; timestamp: string; url: [string] }, FormData>({
            query: body => ({
                url: `upload_to_s3_and_get_obj_url`,
                method: "POST",
                body: body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
        }),

        sendChargerAlert: build.mutation<{ message: string; success: boolean }, FormData>({
            query: body => ({
                url: `charger_alert`,
                method: "POST",
                body: body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const res = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: res.data.message }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
        }),

        sendSlackAlert: build.mutation<
            { message: string; status: string },
            {
                ticket_id: string
                channel_id: string
                remarks: string
                user_ids: string[]
            }
        >({
            query: body => ({
                url: `notify_pod`,
                method: "POST",
                body: body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const res = await queryFulfilled
                    if (res.data.status === "error") {
                        dispatch(setAlertFunc({ type: "error", msg: res.data.message }))
                    } else {
                        dispatch(
                            setAlertFunc({ type: "success", msg: res.data.message || "Slack alert sent successfully." })
                        )
                    }
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
        }),

        addBOQ: build.mutation<
            { boq_id: number; msg: string },
            {
                project_id: number
                ticket_id: number
                reference_type: "project"
                vendor_id?: number
                module_id: number
                submodule_id: number
                items: BOQItemsPayload[]
                is_survey?: "False"
                skipTagInvalidation?: boolean
            }
        >({
            query: ({ skipTagInvalidation, ...body }) => ({
                url: `cristi_boq`,
                useV3: true,
                method: "POST",
                body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const res = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: res.data.msg }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.error?.data?.error || "Something went wrong!" }))
                }
            },
            invalidatesTags: (result, error, arg) => (arg.skipTagInvalidation ? [] : ["Installation"]),
        }),
        editBOQ: build.mutation<
            { boq_id: number; msg: string },
            {
                boq_id: number
                project_id: number
                ticket_id: number
                reference_type: "project"
                vendor_id?: number
                module_id: number
                submodule_id: number
                items: BOQItemsPayload[]
            }
        >({
            query: body => ({
                url: `cristi_boq`,
                method: "PUT",
                useV3: true,
                body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const res = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: res.data.msg }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
            invalidatesTags: [
                // { type: "Survey", id: "BOQ" },
                { type: "Installation", id: "BOQ" },
            ],
        }),

        editBoqStatus: build.mutation<
            { msg: string },
            {
                boq_id: number
                project_id: number
                new_status: string
                is_super_admin?: boolean
                skipTagInvalidation?: boolean
            }
        >({
            query: ({ skipTagInvalidation, ...body }) => ({
                url: `boq_status`,
                method: "PUT",
                useV3: true,
                body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const res = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: res.data.msg }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
            invalidatesTags: (result, error, arg) =>
                arg.skipTagInvalidation ? [] : [{ type: "Installation", id: "BOQ" }, "Installation"],
        }),
        deleteBOQ: build.mutation<
            { message: string },
            {
                boq_id: number
            }
        >({
            query: body => ({
                url: `delete_boq`,
                method: "DELETE",
                body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const res = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: res.data.message }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
            invalidatesTags: [
                // { type: "Survey", id: "BOQ" },
                { type: "Installation", id: "BOQ" },
            ],
        }),

        getBoqItemRemarks: build.query<
            { item_fileurl: [FileObj] },
            {
                boq_id: number
                item_id: number
            }
        >({
            query: params => ({
                url: `get_item_file_url`,
                method: "GET",
                params,
            }),
        }),

        uploadBoqItemRemarks: build.mutation<{ msg: string }, FormData>({
            query: body => ({
                url: `upload_item_images`,
                method: "POST",
                body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const res = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: res.data.msg || "File uploaded successfully" }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
            invalidatesTags: [
                // { type: "Survey", id: "BOQ" },
                { type: "Installation", id: "BOQ" },
            ],
        }),

        // Old vendor list to be removed later
        getVendorsList: build.query<
            any,
            {
                search_key: string
                page: number
                per_page: number
                service?: number[]
            }
        >({
            query: ({ search_key, page, per_page, service }) => ({
                url: `accounts/get_vendors_list`,
                params: {
                    search_key,
                    page,
                    per_page,
                    service: service?.length ? JSON.stringify(service) : "",
                },
            }),
            providesTags: ["Accounts"],
        }),

        getVendorsDropdown: build.query<
            {
                data: { vendor_id: number; account_name: string }[]
                have_more: boolean
                page: number
                page_size: number
            },
            { search_key?: string; service?: number[]; page: number }
        >({
            query: ({ search_key = "", service, page }) => ({
                url: `accounts/get_vendors_dropdown`,
                params: {
                    search_key,
                    service: service?.length ? JSON.stringify(service) : "",
                    page,
                    page_size: 20,
                },
            }),
        }),

        updateGRN: build.mutation<
            { msg: string },
            FormData
            // item_id, boq_id, grn_documents, invoice_no, invoice_date
        >({
            query: body => ({
                url: `update_grn_info`,
                method: "POST",
                useV3: true,
                body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const res = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: res.data.msg }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
            invalidatesTags: [
                { type: "Survey", id: "BOQ" },
                { type: "Installation", id: "BOQ" },
            ],
        }),
        exportIndividualBOQ: build.mutation<
            | {
                  mail: {
                      msg: string
                      to: string[]
                  }
              }
            | { message: string },
            {
                boq_id: number
                cta: "email" | "download"
                emails?: string[]
                username?: string
            }
        >({
            query: body => ({
                url: `export_individual_boq_excel?boq_id=${body.boq_id}`,
                method: "POST",
                useV3: true,
                body,
                responseHandler: async response => {
                    const contentType = response.headers.get("content-type")
                    if (
                        contentType &&
                        contentType.includes("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")
                    ) {
                        const blob = await response.blob()
                        const url = window.URL.createObjectURL(blob)
                        const link = document.createElement("a")
                        link.href = url
                        link.download = `BOQ_${body.boq_id}.xlsx`
                        document.body.appendChild(link)
                        link.click()
                        document.body.removeChild(link)
                        window.URL.revokeObjectURL(url)
                        return { message: "File downloaded successfully" }
                    }
                    return response.json()
                },
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const res: any = await queryFulfilled
                    dispatch(
                        setAlertFunc({
                            type: "success",
                            msg: res.data?.message
                                ? res.data.message
                                : `${res.data.mail.msg} ${res.data.mail.to.join(", ")}`,
                        })
                    )
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
        }),

        exportBOQ: build.mutation<
            {
                mail:
                    | {
                          msg: string
                          to: string[]
                      }
                    | { message: string }
            },
            {
                project_id: number
                cta: "export" | "download"
                ticket_id?: number
                emails?: string[]
                username?: string
            }
        >({
            query: body => ({
                url: `export_boq_grand_total`,
                method: "POST",
                useV3: true,
                body,
                responseHandler: async response => {
                    const contentType = response.headers.get("content-type")
                    if (
                        contentType &&
                        contentType.includes("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")
                    ) {
                        const blob = await response.blob()
                        const url = window.URL.createObjectURL(blob)
                        const link = document.createElement("a")
                        link.href = url
                        link.download = `BOQ_${body.project_id}.xlsx`
                        document.body.appendChild(link)
                        link.click()
                        document.body.removeChild(link)
                        window.URL.revokeObjectURL(url)
                        return { message: "File downloaded successfully" }
                    }
                    return response.json()
                },
            }),
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                try {
                    const res: any = await queryFulfilled
                    dispatch(
                        setAlertFunc({
                            type: "success",
                            msg: res.data?.message
                                ? res.data.message
                                : `${res.data.mail.msg} ${res.data.mail.to.join(", ")}`,
                        })
                    )
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
        }),

        bulkUploadBOQ: build.mutation<
            { parsed_items: any[] },
            FormData
            // File
        >({
            query: body => ({
                url: `boq_bulk_upload`,
                method: "POST",
                body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled
                    enqueueSnackbar("File uploaded successfully.", { variant: "success", autoHideDuration: 3000 })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.error, { variant: "error", autoHideDuration: 3000 })
                }
            },
        }),

        bulkUpdateDrBoqMaterials: build.mutation<
            {
                message: string
            },
            FormData
        >({
            query: body => ({
                url: `dispatch_request/upload_dr`,
                method: "POST",
                useV3: true,
                body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const res = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: res.data.message }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
            invalidatesTags: ["Installation", "Demolition", "Logistics", "Finance"],
        }),

        addReverseDrBoqMaterials: build.mutation<
            {
                message: string
            },
            FormData
        >({
            query: body => ({
                url: `/reverse_logistics`,
                method: "POST",
                useV3: true,
                body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const res = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: res.data.message }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
            invalidatesTags: ["Installation", "Demolition", "Logistics", "Finance"],
        }),
        addPurchaseOrder: build.mutation<
            {
                message?: string
                msg?: string
                po_id: number
                terms_saved?: boolean
                terms_version?: number
                terms_error?: string
            },
            FormData
        >({
            query: body => ({
                url: `purchase_orders`,
                method: "POST",
                useV3: true,
                body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const res = await queryFulfilled
                    enqueueSnackbar(res?.data?.msg || res?.data?.message || "PO added successfully", {
                        variant: "success",
                        autoHideDuration: 3000,
                    })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.error || "Something went wrong!", {
                        variant: "error",
                        autoHideDuration: 3000,
                    })
                }
            },
            invalidatesTags: ["Projects"],
        }),
        updatePurchaseOrder: build.mutation<{ msg: string; po_id: number }, FormData | Record<string, any>>({
            query: body => ({
                url: `purchase_orders`,
                method: "PUT",
                useV3: true,
                body,
            }),
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                try {
                    const res = await queryFulfilled
                    enqueueSnackbar(res.data?.msg || "Purchase Order updated", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.error || error?.error?.data?.msg || "Something went wrong!", {
                        variant: "error",
                    })
                }
            },
            invalidatesTags: ["Projects"],
        }),
        deletePurchaseOrder: build.mutation<{ msg: string; po_id: number }, { po_id: number }>({
            query: params => ({
                url: `purchase_orders`,
                method: "DELETE",
                useV3: true,
                params,
            }),
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                try {
                    const res = await queryFulfilled
                    enqueueSnackbar(res.data?.msg || "Purchase Order deleted", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.error || error?.error?.data?.msg || "Something went wrong!", {
                        variant: "error",
                    })
                }
            },
            invalidatesTags: ["Projects"],
        }),
        updateChargerDetailData: build.mutation<any, Record<string, unknown>>({
            query: body => ({
                url: `charger_details`,
                method: "PUT",
                useV3: true,
                body: body,
            }),
            invalidatesTags: (_, __, args: any) => (args?.clearCache ? ["Logistics"] : []),
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(`${data?.msg}`, { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(`${error?.error?.data?.message || "Something went wrong!"}`, { variant: "error" })
                }
            },
        }),
        getPayoutDealTerms: build.query<{ id: string; name: string }[], void>({
            query: () => ({
                url: `deal_terms`,
                useV3: true,
            }),
            transformResponse: (res: { data: { id: string; name: string }[] }) => res.data,
        }),
        getOnHoldReasons: build.query<{ id: number; reason: string }[], { moduleId: EModuleIds }>({
            query: ({ moduleId }) => ({
                url: `module_reasons`,
                useV3: true,
                params: {
                    module_id: moduleId,
                },
            }),
            transformResponse: (res: { data: { id: number; reason: string }[] }) => res.data,
        }),
        putTicketOnHold: build.mutation<
            { message: string },
            {
                project_id: number
                ticket_id: number
                on_hold: boolean
                module_id: number
                reason_id?: number
                standby?: string
                tentative_date?: string
                tags?: TagTypes[]
            }
        >({
            query: body => ({
                url: `ticket_on_hold`,
                method: "POST",
                body,
                useV3: true,
            }),
            onQueryStarted: async (arg, { queryFulfilled }) => {
                await queryFulfilled
                if (arg.on_hold) {
                    enqueueSnackbar("Project has been put on hold successfully!", { variant: "success" })
                } else {
                    enqueueSnackbar("Project resumed successfully!", { variant: "success" })
                }
            },
            invalidatesTags: (result, error, arg) => arg?.tags || [],
        }),
        getPlpStatus: build.query<{ id: number; title: string }[], void>({
            query: () => ({
                url: `plp_status`,
                useV3: true,
            }),
        }),
        getSurveyTicket: build.query<any[], { ticket_id: string; user_id: number }>({
            query: ({ ticket_id, user_id }) => ({
                url: `surveys/ticket`,
                params: { ticket_id, user_id },
                useV3: true,
            }),
        }),
        editSurveyPlannedDate: build.mutation<{ message: string }, Record<string, any>>({
            query: body => ({
                url: `surveys/ticket`,
                method: "PUT",
                body: body,
                useV3: true,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.message }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: (result, error, arg) => ["Survey"],
        }),
        // Vendor Bills APIs
        fetchVendorBills: build.query<
            VendorBill[],
            {
                bill_id?: number
                po_id?: number
                project_id?: number
                sub_ticket_id?: number
                module_id?: EModuleIds
                sub_module_id?: ESubModuleIds
                raised_by_id?: number
                status?: string
            }
        >({
            query: params => ({
                url: `vendor_bills`,
                useV3: true,
                params,
            }),
            transformResponse: (res: VendorBillsResponse) => res?.data ?? [],
            providesTags: ["Installation", "Survey", "EvApplication"],
        }),

        createVendorBill: build.mutation<{ msg: string; bill_id: number; payment_id: number }, FormData>({
            query: body => ({
                url: `vendor_bills`,
                method: "POST",
                useV3: true,
                body,
            }),
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                try {
                    const res = await queryFulfilled
                    enqueueSnackbar(res.data?.msg || "Vendor bill created", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.error || error?.error?.data?.msg || "Something went wrong!", {
                        variant: "error",
                    })
                }
            },
            invalidatesTags: ["Installation", "Survey", "EvApplication"],
        }),

        updateVendorBill: build.mutation<{ msg: string; bill_id: number }, FormData | Record<string, any>>({
            query: body => ({
                url: `vendor_bills`,
                method: "PUT",
                useV3: true,
                body,
            }),
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                try {
                    const res = await queryFulfilled
                    enqueueSnackbar(res.data?.msg || "Vendor bill updated", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.error || error?.error?.data?.msg || "Something went wrong!", {
                        variant: "error",
                    })
                }
            },
            invalidatesTags: ["Installation", "Survey", "EvApplication"],
        }),

        deleteVendorBill: build.mutation<{ msg: string; bill_id: number }, { bill_id: number }>({
            query: params => ({
                url: `vendor_bills`,
                method: "DELETE",
                useV3: true,
                params,
            }),
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                try {
                    const res = await queryFulfilled
                    enqueueSnackbar(res.data?.msg || "Vendor bill deleted", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.error || error?.error?.data?.msg || "Something went wrong!", {
                        variant: "error",
                    })
                }
            },
            invalidatesTags: ["Installation", "Survey", "EvApplication"],
        }),

        // Vendor Payment APIs
        fetchVendorPayments: build.query<
            VendorPayment[],
            {
                payment_id?: number
                bill_id?: number
                po_id?: number
                project_id?: number
                sub_ticket_id?: number
                module_id?: EModuleIds
                sub_module_id?: ESubModuleIds
                payment_status?: string
                invoice_booked?: 0 | 1
            }
        >({
            query: params => ({
                url: `vendor_payments`,
                useV3: true,
                params,
            }),
            transformResponse: (res: VendorPaymentsResponse) => res?.data ?? [],
            providesTags: ["Installation", "Survey"],
        }),

        createVendorPayment: build.mutation<{ msg: string; payment_id: number }, FormData>({
            query: body => ({
                url: `vendor_payments`,
                method: "POST",
                useV3: true,
                body,
            }),
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                try {
                    const res = await queryFulfilled
                    enqueueSnackbar(res.data?.msg || "Vendor payment created", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.error || error?.error?.data?.msg || "Something went wrong!", {
                        variant: "error",
                    })
                }
            },
            invalidatesTags: ["Installation", "Survey"],
        }),

        updateVendorPayment: build.mutation<{ msg: string; payment_id: number }, FormData>({
            query: body => ({
                url: `vendor_payments`,
                method: "PUT",
                useV3: true,
                body,
            }),
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                try {
                    const res = await queryFulfilled
                    enqueueSnackbar(res.data?.msg || "Vendor payment updated", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.error || error?.error?.data?.msg || "Something went wrong!", {
                        variant: "error",
                    })
                }
            },
            invalidatesTags: ["Installation", "Survey"],
        }),

        deleteVendorPayment: build.mutation<{ msg: string; payment_id: number }, { payment_id: number }>({
            query: params => ({
                url: `vendor_payments`,
                method: "DELETE",
                useV3: true,
                params,
            }),
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                try {
                    const res = await queryFulfilled
                    enqueueSnackbar(res.data?.msg || "Vendor payment deleted", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.error || error?.error?.data?.msg || "Something went wrong!", {
                        variant: "error",
                    })
                }
            },
            invalidatesTags: ["Installation", "Survey"],
        }),
        getComments: build.query<any, any>({
            query: (params: any) => ({
                url: `/comments/comment`,
                params: params,
                useV3: true,
            }),
            providesTags: [{ type: "Installation", id: "ticketUpadte" }],
        }),
        addComments: build.mutation<any, any>({
            query: body => ({
                url: `/comments/comment`,
                method: "POST",
                useV3: true,
                body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled

                    dispatch(setAlertFunc({ type: "success", msg: data?.message }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message }))
                }
            },
            invalidatesTags: [{ type: "Installation", id: "ticketUpadte" }],
        }),

        getChangeOrders: build.query<
            ChangeOrderObj[],
            { ticket_id: number | string; module_id: number; submodule_id?: number; status?: string }
        >({
            query: params => ({
                url: `change_orders`,
                params,
                useV3: true,
            }),
            providesTags: ["Installation", "Projects"],
        }),

        createChangeOrder: build.mutation<
            { message: string; data?: any; success?: boolean },
            {
                ticket_id: number
                project_id: number | string
                module_id: number
                submodule_id?: number
                reason_id: number
                remarks: string
                requested_by: number
                assigned_to: number
                requested_at: string
            }
        >({
            query: body => ({
                url: `change_orders`,
                method: "POST",
                body,
                useV3: true,
            }),
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(
                        setAlertFunc({
                            type: "success",
                            msg: data?.message || "Change order request created successfully",
                        })
                    )
                } catch (error: any) {
                    dispatch(
                        setAlertFunc({
                            type: "error",
                            msg: error?.error?.data?.message || error?.message || "Failed to create change order",
                        })
                    )
                }
            },
            invalidatesTags: (_result, _error, arg) => [
                { type: "Installation", id: String(arg.ticket_id) },
                "Installation",
                "Projects",
            ],
        }),

        acceptChangeOrder: build.mutation<
            { message: string; data?: any; success?: boolean },
            { change_order_id: number; user_id: number }
        >({
            query: body => ({
                url: `change_orders/accept`,
                method: "PUT",
                body,
                useV3: true,
            }),
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(
                        setAlertFunc({
                            type: "success",
                            msg: data?.message || "Change order accepted successfully",
                        })
                    )
                } catch (error: any) {
                    dispatch(
                        setAlertFunc({
                            type: "error",
                            msg: error?.error?.data?.message || error?.message || "Failed to accept change order",
                        })
                    )
                }
            },
            invalidatesTags: ["Installation", "Projects"],
        }),

        getDfcApproval: build.query<
            DfcApprovalResponse,
            { ticket_id: string | number; module_id?: number; submodule_id?: number }
        >({
            query: params => ({
                url: `approvals/dfc`,
                params,
                useV3: true,
            }),
            providesTags: (_result, _error, arg) => [{ type: "Survey", id: `dfc_approval_${arg.ticket_id}` }],
        }),

        createDfcApproval: build.mutation<{ message: string }, { ticket_id: string | number; body: FormData }>({
            query: ({ body }) => ({
                url: `approvals/create`,
                method: "POST",
                body,
                useV3: true,
            }),
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.message || "Submitted for DFC Approval successfully", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(
                        error?.error?.data?.message || error?.error?.data?.error || "Something went wrong!",
                        { variant: "error" }
                    )
                }
            },
            invalidatesTags: (_result, _error, arg) => [{ type: "Survey", id: `dfc_approval_${arg.ticket_id}` }],
        }),

        // Edit an already-raised DFC approval: updates the feedback response, location & deal
        // details only (approvers/remarks/document are fixed at creation). JSON body.
        editDfcApproval: build.mutation<
            { message: string },
            {
                ticket_id: string | number
                body: {
                    sub_ticket_id: number
                    module_id: number
                    submodule_id: number
                    user_id: number
                    response_id?: number | null
                    answers: { question_id: number; answer: string }[]
                    location_details: {
                        google_maps_link: string
                        location_type: string
                        location_subtype: string
                        vehicle_movement: string[]
                    }
                    deal_details: Record<string, any>
                }
            }
        >({
            query: ({ body }) => ({
                url: `feedback-forms/form/ticket/submit`,
                method: "POST",
                body,
                useV3: true,
            }),
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.message || "DFC Approval updated successfully", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(
                        error?.error?.data?.message || error?.error?.data?.error || "Something went wrong!",
                        { variant: "error" }
                    )
                }
            },
            invalidatesTags: (_result, _error, arg) => [{ type: "Survey", id: `dfc_approval_${arg.ticket_id}` }],
        }),

        markWelcomeSeen: build.mutation<{ msg: string }, { user_id: number }>({
            query: body => ({
                url: `welcome_seen`,
                method: "POST",
                useV3: true,
                body,
            }),
        }),

        // Update assigned_to (Executed By) on a ticket's project_tickets_mapping row
        updateTicketAssignedTo: build.mutation<
            { message: string },
            {
                ticket_id: number
                module_id: EModuleIds
                submodule_id?: ESubModuleIds
                assigned_to: number
                skipTagInvalidation?: boolean
            }
        >({
            query: ({ skipTagInvalidation, ...body }) => ({
                url: `tickets/assigned_to`,
                method: "PUT",
                useV3: true,
                body,
            }),
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                try {
                    const res = await queryFulfilled
                    enqueueSnackbar(res.data?.message || "Executed By updated successfully", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(
                        error?.error?.data?.error || error?.error?.data?.message || "Something went wrong!",
                        {
                            variant: "error",
                        }
                    )
                }
            },
            invalidatesTags: (result, error, arg) => {
                if (arg.skipTagInvalidation) return []
                if (arg.submodule_id === ESubModuleIds.DEMOLITION) return ["Demolition"]
                return ["Projects"]
            },
        }),

        // Download the bulk upload Excel template for a given entity (site/account)
        downloadBulkUploadTemplate: build.query<{ message: string }, { entity: "site" | "account" }>({
            query: ({ entity }) => ({
                url: `bulk_upload_template`,
                useV3: true,
                params: { entity },
                responseHandler: async response => {
                    const contentType = response.headers.get("content-type") || ""
                    if (!contentType.includes("application/json")) {
                        const blob = await response.blob()
                        const url = window.URL.createObjectURL(blob)
                        const link = document.createElement("a")
                        link.href = url
                        link.download = `${entity}_bulk_upload_template.xlsx`
                        document.body.appendChild(link)
                        link.click()
                        document.body.removeChild(link)
                        window.URL.revokeObjectURL(url)
                        return { message: "Template downloaded successfully" }
                    }
                    return response.json()
                },
            }),
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                try {
                    await queryFulfilled
                } catch (error: any) {
                    enqueueSnackbar(
                        error?.error?.data?.error || error?.error?.data?.message || "Failed to download template",
                        { variant: "error", autoHideDuration: 3000 }
                    )
                }
            },
        }),

        // Bulk insert records (site/account) from an uploaded Excel file
        // FormData -> file, entity ; user_id is appended automatically by the base query
        bulkInsert: build.mutation<{ message?: string; msg?: string; success?: boolean }, FormData>({
            query: body => ({
                url: `bulk_insert`,
                method: "POST",
                useV3: true,
                body,
            }),
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                try {
                    const res = await queryFulfilled
                    enqueueSnackbar(res?.data?.message || res?.data?.msg || "File uploaded successfully", {
                        variant: "success",
                        autoHideDuration: 3000,
                    })
                } catch (error: any) {
                    enqueueSnackbar(
                        error?.error?.data?.error || error?.error?.data?.message || "Something went wrong!",
                        { variant: "error", autoHideDuration: 3000 }
                    )
                }
            },
            invalidatesTags: ["Accounts", "Sites"],
        }),

        // Paginated status list of bulk upload tasks for a given entity (site/account)
        getBulkTaskStatus: build.query<
            BulkTaskStatusResponse,
            { entity: "site" | "account"; page?: number; page_size?: number }
        >({
            query: ({ entity, page = 1, page_size = 5 }) => ({
                url: `bulk_task_status`,
                useV3: true,
                params: { entity, page, page_size },
            }),
            providesTags: (_result, _error, arg) => (arg.entity === "site" ? ["Sites"] : ["Accounts"]),
        }),
    }),
})

export const {
    useLazyGetChargerDetailsQuery,
    useLazyGetChargerDetailsV3Query,
    useLazyGetChargerMakeDeatilsQuery,
    useGetStatesQuery,
    useGetCitiesQuery,
    useGetCountriesQuery,
    useGetCPOAccountNamesQuery,
    useGetHostNamesQuery,
    useGetChargersTypeQuery,
    useGetAllStatusQuery,
    useGetAllTagsQuery,
    useGetAssignedToFilterOptionsQuery,
    useGetAssignedToListOptionsQuery,
    useGetModuleUsersQuery,
    useGetVendorListQuery,
    useGetAmenitiesQuery,
    useLazyGetVendorsListQuery,
    useGetDiscomsQuery,
    useGetBrandsQuery,
    useGetStakeholdersRoleQuery,
    useGetCategoriesQuery,
    useGetChargerSkuQuery,
    useGetReasonsQuery,
    useGetTimeOfDayQuery,
    useGetSurveyLinkedToProjectQuery,
    useGetOEMQuery,
    useGetUOMQuery,
    useGetChargerMakeQuery,
    useGetConnectorsQuery,
    useGetStandardItemsQuery,
    useLazyGetGrnDetailsQuery,
    useLazyGetBoqItemRemarksQuery,
    useGetCPOQuery,
    useGetBOQQuery,
    useLazyGetBOQQuery,
    useGetModuleTagsQuery,
    useLazyGetDrBoqMaterialsQuery,
    useFetchExistingBOQsQuery,
    useGetBoqGrandTotalQuery,
    useGetDocumentTemplatesQuery,
    useGetDocumentsDataQuery,
    useGetReasonsForBlockingQuery,
    useGetOpsHistoryQuery,
    useGetCommentsQuery,
    useGetAllLabelsQuery,
    useLazyGetCountryStatesQuery,
    useGetCountryStatesQuery,
    useGetSlackUsersQuery,
    useGetSlackChannelsQuery,
    useGetContactTypesQuery,
    useLazyGetLabelsQuery,
    useUpdatePrimaryLabelMutation,
    useGetDealDoneByUsersQuery,
    useDeleteLabelMutationMutation,
    useUpdateLabelsInBulkMutation,
    useUpdateChargerDetailsMutation,
    useUpdateChargerDetailsV3Mutation,
    useMapLabelsMutation,
    useClearLabelsMutation,
    useGetFileViewUrlMutation,
    useLazyGetHostingAddressQuery,
    useUploadToS3AndGetObjUrlMutation,
    useSendChargerAlertMutation,
    useMapTagToTicketMutation,
    useUpdatePrimaryTagMutation,
    useUnmapTagFromTicketMutation,
    useCreateTagToTicketMutation,
    useSendSlackAlertMutation,
    useExportBOQMutation,
    useAddBOQMutation,
    useEditBOQMutation,
    useEditBoqStatusMutation,
    useDeleteBOQMutation,
    useGetProgressHistoryQuery,
    useUpdateProgressPercentageMutation,
    useUpdateGRNMutation,
    useExportIndividualBOQMutation,
    useBulkUploadBOQMutation,
    useUploadBoqItemRemarksMutation,
    useBulkUpdateDrBoqMaterialsMutation,
    useGetParsedDrQuery,
    useGetOnHoldReasonsQuery,
    useLazyDownloadParsedDrQuery,
    useLazyGetDrCreationStatusQuery,
    useLazyGetReverseDrCreationStatusQuery,
    useLazyGetDemolitionDrCreationStatusQuery,
    useLazyGetReverseDrBoqMaterialsQuery,
    useAddReverseDrBoqMaterialsMutation,
    useGetAllSitesListQuery,
    useGetAllAccountsListQuery,
    useLazyGetAccountsCompactedQuery,
    useLazyGetSitesCompactedQuery,
    useGetAccountRolesQuery,
    useAddPurchaseOrderMutation,
    useUpdatePurchaseOrderMutation,
    useDeletePurchaseOrderMutation,
    useLazyGetPurchaseOrdersQuery,
    useGetAllProgramsListQuery,
    useGetProjectCategoriesQuery,
    useGetProductsQuery,
    useLazyGetProductsCompactedQuery,
    useGetAccountBySiteIdQuery,
    useLazyGetAccountBySiteIdQuery,
    useGetInfraSowsQuery,
    useGetPurchaseOrdersQuery,
    useGetDocumentTypesQuery,
    useGetCoBrandedListQuery,
    useGetCinsBySkuAndProductQuery,
    useLazyGetCinsBySkuAndProductQuery,
    useUpdateChargerDetailDataMutation,
    useGetCpoListQuery,
    useGetPayoutDealTermsQuery,
    usePutTicketOnHoldMutation,
    useGetPlpStatusQuery,
    useGetSurveyTicketQuery,
    useGetRolesQuery,
    useEditSurveyPlannedDateMutation,
    useFetchVendorBillsQuery,
    useCreateVendorBillMutation,
    useUpdateVendorBillMutation,
    useDeleteVendorBillMutation,
    useFetchVendorPaymentsQuery,
    useCreateVendorPaymentMutation,
    useUpdateVendorPaymentMutation,
    useDeleteVendorPaymentMutation,
    useAddCommentsMutation,
    useGetChangeOrdersQuery,
    useLazyGetChangeOrdersQuery,
    useCreateChangeOrderMutation,
    useAcceptChangeOrderMutation,
    useUpdateTicketAssignedToMutation,
    useMarkWelcomeSeenMutation,
    useGetDfcApprovalQuery,
    useCreateDfcApprovalMutation,
    useEditDfcApprovalMutation,
    useLazyGetVendorsDropdownQuery,
    useLazyDownloadBulkUploadTemplateQuery,
    useBulkInsertMutation,
    useGetBulkTaskStatusQuery,
} = commonService

export type { ChangeOrderObj }
